/**
 * Procedurally generated textures. Nothing here is downloaded — every byte is computed
 * at boot, which keeps the payload small (a hard constraint for the iOS memory envelope)
 * and lets us tune the sea's character by editing numbers rather than sourcing art.
 */

import { RawTexture } from '@babylonjs/core/Materials/Textures/rawTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Constants } from '@babylonjs/core/Engines/constants';
import type { Scene } from '@babylonjs/core/scene';
import { Rng } from '../sim/rng';

/** Smootherstep — C2 continuous, so noise octaves show no lattice creases. */
const fade = (t: number): number => t * t * t * (t * (t * 6 - 15) + 10);

const halfScratchF32 = new Float32Array(1);
const halfScratchU32 = new Uint32Array(halfScratchF32.buffer);

/** IEEE 754 float32 → float16 bits, round-to-nearest. */
function floatToHalf(value: number): number {
  halfScratchF32[0] = value;
  const bits = halfScratchU32[0] as number;
  const sign = (bits >>> 16) & 0x8000;
  const exponent = ((bits >>> 23) & 0xff) - 127 + 15;
  const mantissa = bits & 0x7fffff;
  if (exponent >= 0x1f) return sign | 0x7c00;
  if (exponent <= 0) {
    // Subnormal halves resolve below 6e-5 — anything smaller is zero here.
    if (exponent < -10) return sign;
    return sign | ((((mantissa | 0x800000) >> (1 - exponent)) + 0x1000) >> 13);
  }
  // Mantissa rounding may carry into the exponent field; adjacent bit layout makes
  // that carry produce exactly the next representable half, so plain addition is right.
  return sign | ((exponent << 10) + ((mantissa + 0x1000) >> 13));
}

/**
 * Tileable value noise. The lattice wraps at `freq`, so the resulting texture repeats
 * seamlessly — essential because these tile across kilometres of ocean.
 */
function tileableNoise(size: number, freq: number, rng: Rng): Float32Array {
  const lattice = new Float32Array(freq * freq);
  for (let i = 0; i < lattice.length; i++) lattice[i] = rng.next();

  const out = new Float32Array(size * size);
  const scale = freq / size;

  for (let y = 0; y < size; y++) {
    const fy = y * scale;
    const y0 = Math.floor(fy) % freq;
    const y1 = (y0 + 1) % freq;
    const ty = fade(fy - Math.floor(fy));

    for (let x = 0; x < size; x++) {
      const fx = x * scale;
      const x0 = Math.floor(fx) % freq;
      const x1 = (x0 + 1) % freq;
      const tx = fade(fx - Math.floor(fx));

      const v00 = lattice[y0 * freq + x0] as number;
      const v10 = lattice[y0 * freq + x1] as number;
      const v01 = lattice[y1 * freq + x0] as number;
      const v11 = lattice[y1 * freq + x1] as number;

      const a = v00 + (v10 - v00) * tx;
      const b = v01 + (v11 - v01) * tx;
      out[y * size + x] = a + (b - a) * ty;
    }
  }
  return out;
}

/**
 * Tileable gradient (Perlin) noise.
 *
 * Value noise — random scalars on a lattice, blended — carries the lattice in its own
 * structure: every cell centre is an extremum, so magnified it reads as soft polygons
 * with axis-aligned seams. That is exactly what the night sky was showing, because the
 * cloud base layer is a 3x3 lattice stretched across the whole dome.
 *
 * Gradient noise puts a random *direction* at each lattice point and interpolates the dot
 * product with the offset, so the value at every lattice point is zero and the extrema
 * fall between them. No plateaus, no visible grid. Wrapping the lattice indices keeps it
 * exactly tileable, which the sky dome needs.
 */
function tileableGradientNoise(size: number, freq: number, rng: Rng): Float32Array {
  const gx = new Float32Array(freq * freq);
  const gy = new Float32Array(freq * freq);
  for (let i = 0; i < gx.length; i++) {
    const a = rng.next() * Math.PI * 2;
    gx[i] = Math.cos(a);
    gy[i] = Math.sin(a);
  }

  const out = new Float32Array(size * size);
  const scale = freq / size;

  for (let y = 0; y < size; y++) {
    const fy = y * scale;
    const iy = Math.floor(fy);
    const dy = fy - iy;
    const y0 = ((iy % freq) + freq) % freq;
    const y1 = (y0 + 1) % freq;
    const ty = fade(dy);

    for (let x = 0; x < size; x++) {
      const fx = x * scale;
      const ix = Math.floor(fx);
      const dx = fx - ix;
      const x0 = ((ix % freq) + freq) % freq;
      const x1 = (x0 + 1) % freq;
      const tx = fade(dx);

      const i00 = y0 * freq + x0, i10 = y0 * freq + x1;
      const i01 = y1 * freq + x0, i11 = y1 * freq + x1;

      const n00 = (gx[i00] as number) * dx + (gy[i00] as number) * dy;
      const n10 = (gx[i10] as number) * (dx - 1) + (gy[i10] as number) * dy;
      const n01 = (gx[i01] as number) * dx + (gy[i01] as number) * (dy - 1);
      const n11 = (gx[i11] as number) * (dx - 1) + (gy[i11] as number) * (dy - 1);

      const a = n00 + (n10 - n00) * tx;
      const b = n01 + (n11 - n01) * tx;
      // Gradient noise is signed and roughly +/-0.7; remap to 0..1.
      out[y * size + x] = (a + (b - a) * ty) * 0.7 + 0.5;
    }
  }
  return out;
}

/**
 * Domain-warped gradient fBm.
 *
 * Warping the lookup by another noise field before sampling is what turns smooth hills
 * into the torn, curdled shapes real cloud decks have (plan C2 calls for exactly this).
 * Without it even good noise reads as an even blanket.
 */
function warpedFbm(
  size: number, baseFreq: number, octaves: number, rng: Rng, gain: number, warp: number,
): Float32Array {
  const warpX = tileableGradientNoise(size, Math.max(2, Math.round(baseFreq * 1.5)), rng);
  const warpY = tileableGradientNoise(size, Math.max(2, Math.round(baseFreq * 1.5)), rng);

  const layers: Float32Array[] = [];
  const amps: number[] = [];
  let amp = 1;
  let total = 0;
  for (let o = 0; o < octaves; o++) {
    const freq = Math.round(baseFreq * Math.pow(2, o));
    if (freq > size) break;
    layers.push(tileableGradientNoise(size, freq, rng));
    amps.push(amp);
    total += amp;
    amp *= gain;
  }

  const out = new Float32Array(size * size);
  const strength = warp * size;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = y * size + x;
      // Displace the sample point by the warp field, wrapped so the result still tiles.
      const ox = Math.round(x + ((warpX[i] as number) - 0.5) * strength);
      const oy = Math.round(y + ((warpY[i] as number) - 0.5) * strength);
      const sx = ((ox % size) + size) % size;
      const sy = ((oy % size) + size) % size;
      const j = sy * size + sx;

      let v = 0;
      for (let o = 0; o < layers.length; o++) {
        v += ((layers[o] as Float32Array)[j] as number) * (amps[o] as number);
      }
      out[i] = v / total;
    }
  }
  return out;
}

/** Sum octaves of tileable noise. Each octave doubles frequency and halves amplitude. */
function fbm(size: number, baseFreq: number, octaves: number, rng: Rng, gain = 0.5): Float32Array {
  const out = new Float32Array(size * size);
  let amp = 1;
  let total = 0;
  for (let o = 0; o < octaves; o++) {
    const freq = baseFreq * Math.pow(2, o);
    if (freq > size) break;
    const layer = tileableNoise(size, freq, rng);
    for (let i = 0; i < out.length; i++) out[i] += (layer[i] as number) * amp;
    total += amp;
    amp *= gain;
  }
  for (let i = 0; i < out.length; i++) out[i] /= total;
  return out;
}

/**
 * A tileable directional wind-sea packet.
 *
 * Ordinary fBm is isotropic: it makes soft hills with no notion of a travelling crest.
 * Under magnification those hills became broad horizontal slabs. Integer wave vectors
 * keep this field exactly tileable, while low-frequency phase warp and an amplitude
 * envelope bend each crest and open irregular gaps along it.
 */
function directionalWavePacket(size: number, rng: Rng): Float32Array {
  const modes = [
    [10, 0, 0.20],
    [11, 1, 0.15],
    [12, -2, 0.14],
    [13, 3, 0.11],
    [14, -4, 0.09],
    [15, 5, 0.07],
    [16, -5, 0.06],
    [17, 3, 0.05],
    [18, -2, 0.04],
    [19, 6, 0.035],
    [20, -6, 0.025],
  ] as const;
  const phases = modes.map(() => rng.range(0, Math.PI * 2));
  const warp = fbm(size, 2, 4, rng, 0.55);
  const envelope = fbm(size, 3, 4, rng, 0.52);
  const raw = new Float32Array(size * size);
  let min = Infinity;
  let max = -Infinity;

  for (let y = 0; y < size; y++) {
    const v = y / size;
    for (let x = 0; x < size; x++) {
      const u = x / size;
      const i = y * size + x;
      const phaseWarp = ((warp[i] as number) - 0.5) * 1.55;
      const localAmplitude = 0.30 + (envelope[i] as number) * 0.70;
      let h = 0;
      for (let m = 0; m < modes.length; m++) {
        const mode = modes[m] as readonly [number, number, number];
        h += Math.sin(
          Math.PI * 2 * (mode[0] * u + mode[1] * v)
            + (phases[m] as number)
            + phaseWarp * (0.72 + m * 0.12),
        ) * mode[2];
      }
      h *= localAmplitude;
      raw[i] = h;
      min = Math.min(min, h);
      max = Math.max(max, h);
    }
  }

  const span = Math.max(max - min, 1e-6);
  for (let i = 0; i < raw.length; i++) raw[i] = ((raw[i] as number) - min) / span;
  return raw;
}

/**
 * Tileable, domain-warped cellular boundaries.
 *
 * White water does not arrange itself as isolated marble-noise spots. Surface tension
 * gathers it into connected borders around darker patches, much like a constantly
 * deforming Voronoi diagram. The distance difference between the nearest and
 * second-nearest cell sites is exactly that connected border. Low-frequency coordinate
 * warping removes the giveaway straight polygon edges while retaining topology.
 */
function cellularEdgeNetwork(
  size: number,
  cells: number,
  rng: Rng,
  edgeSharpness: number,
  warpStrength: number,
): Float32Array {
  const siteX = new Float32Array(cells * cells);
  const siteY = new Float32Array(cells * cells);
  for (let i = 0; i < siteX.length; i++) {
    siteX[i] = rng.range(0.14, 0.86);
    siteY[i] = rng.range(0.14, 0.86);
  }

  const warpX = fbm(size, 2, 5, rng, 0.56);
  const warpY = fbm(size, 3, 4, rng, 0.54);
  const erosion = fbm(size, Math.max(3, Math.floor(cells * 0.45)), 4, rng, 0.53);
  const out = new Float32Array(size * size);

  const wrapCell = (value: number): number => {
    const wrapped = value % cells;
    return wrapped < 0 ? wrapped + cells : wrapped;
  };

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = y * size + x;
      const px = (x / size) * cells
        + ((warpX[i] as number) - 0.5) * warpStrength;
      const py = (y / size) * cells
        + ((warpY[i] as number) - 0.5) * warpStrength;
      const baseX = Math.floor(px);
      const baseY = Math.floor(py);
      let nearest = Infinity;
      let second = Infinity;

      // Jitter is constrained inside each cell, so the nearest boundary sites remain
      // inside the surrounding 3x3 neighbourhood even after the lookup coordinate is
      // warped. Keeping this tight matters because the field is generated at boot.
      for (let oy = -1; oy <= 1; oy++) {
        for (let ox = -1; ox <= 1; ox++) {
          const cellX = baseX + ox;
          const cellY = baseY + oy;
          const wrappedX = wrapCell(cellX);
          const wrappedY = wrapCell(cellY);
          const siteIndex = wrappedY * cells + wrappedX;
          const sx = cellX + (siteX[siteIndex] as number);
          const sy = cellY + (siteY[siteIndex] as number);
          const dx = px - sx;
          const dy = py - sy;
          const distanceSquared = dx * dx + dy * dy;
          if (distanceSquared < nearest) {
            second = nearest;
            nearest = distanceSquared;
          } else if (distanceSquared < second) {
            second = distanceSquared;
          }
        }
      }

      const edgeDistance = Math.sqrt(second) - Math.sqrt(nearest);
      const filament = Math.exp(-edgeDistance * edgeSharpness);
      // Local erosion opens natural gaps without severing every junction. A small floor
      // preserves the sense that neighbouring foam patches still belong to one field.
      const localErosion = 0.34 + (erosion[i] as number) * 0.78;
      out[i] = Math.min(1, filament * localErosion);
    }
  }

  return out;
}

/**
 * Packed visual-wave field for detail finer than the physical swell geometry resolves.
 *
 *   RG = local height-field slope
 *   B  = height
 *   A  = convex crest/curvature
 *
 * The extra channels let the ocean reuse the exact same deterministic field for meso
 * shading and foam placement instead of evaluating another trigonometric spectrum per
 * fragment. None of these channels displace geometry or participate in buoyancy.
 */
export function createDetailNormalTexture(scene: Scene, size = 256): Texture {
  const rng = new Rng('ocean-detail-normal');
  const height = directionalWavePacket(size, rng);
  const turbulentTail = fbm(size, 8, 5, rng, 0.55);
  // A real wind sea is directional at large scales but increasingly disordered toward
  // the capillary tail. Blending that tail into the same packed field breaks the clean
  // packet rows under scope magnification without losing their overall bearing.
  for (let i = 0; i < height.length; i++) {
    height[i] = (height[i] as number) * 0.68 + (turbulentTail[i] as number) * 0.32;
  }
  const data = new Uint8Array(size * size * 4);
  const crestScore = new Float32Array(size * size);
  const histogram = new Uint32Array(512);

  // Gradient strength: directional crests need less amplification than smooth fBm.
  const strength = 3.8;

  // First pass: derive a continuous high-and-convex crest score. A compact histogram
  // gives stable percentiles without sorting hundreds of thousands of JS numbers at
  // boot. The upper 16% remains a possible crest; roughly the top 3% reaches full white.
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const xm2 = (x - 2 + size) % size;
      const xp2 = (x + 2) % size;
      const ym = (y - 1 + size) % size;
      const yp = (y + 1) % size;

      const h = height[y * size + x] as number;
      // Curvature two texels along the packet's travel direction isolates the narrow
      // lip of a crest. The cross-wind neighbours keep short hooked ends eligible,
      // instead of forcing every line to run unbroken across the tile.
      const neighbourMean = (
        (height[y * size + xm2] as number) * 0.34
        + (height[y * size + xp2] as number) * 0.34
        + (height[ym * size + x] as number) * 0.16
        + (height[yp * size + x] as number) * 0.16
      );
      const convexity = Math.max(0, h - neighbourMean);
      const score = Math.max(0, Math.min(1, (h - 0.50) * 1.05 + convexity * 35));
      const i = y * size + x;
      crestScore[i] = score;
      histogram[Math.min(histogram.length - 1, Math.floor(score * histogram.length))] += 1;
    }
  }

  const percentile = (target: number): number => {
    const wanted = Math.floor(size * size * target);
    let count = 0;
    for (let i = 0; i < histogram.length; i++) {
      count += histogram[i] as number;
      if (count >= wanted) return i / (histogram.length - 1);
    }
    return 1;
  };
  const crestLow = percentile(0.84);
  const crestHigh = Math.max(crestLow + 1 / histogram.length, percentile(0.97));

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const xm = (x - 1 + size) % size;
      const xp = (x + 1) % size;
      const ym = (y - 1 + size) % size;
      const yp = (y + 1) % size;

      const dx = ((height[y * size + xp] as number) - (height[y * size + xm] as number)) * strength;
      const dy = ((height[yp * size + x] as number) - (height[ym * size + x] as number)) * strength;
      const h = height[y * size + x] as number;
      const i = y * size + x;
      const crest = Math.max(
        0,
        Math.min(1, ((crestScore[i] as number) - crestLow) / (crestHigh - crestLow)),
      );

      // Normal of the height field, normalised then packed to [0,1].
      const nx = -dx;
      const ny = -dy;
      const nz = 1;
      const len = Math.hypot(nx, ny, nz);

      const o = i * 4;
      data[o + 0] = Math.round(((nx / len) * 0.5 + 0.5) * 255);
      data[o + 1] = Math.round(((ny / len) * 0.5 + 0.5) * 255);
      data[o + 2] = Math.round(Math.max(0, Math.min(1, h)) * 255);
      data[o + 3] = Math.round(crest * 255);
    }
  }

  const tex = RawTexture.CreateRGBATexture(data, size, size, scene, true, false, Texture.TRILINEAR_SAMPLINGMODE);
  tex.wrapU = Constants.TEXTURE_WRAP_ADDRESSMODE;
  tex.wrapV = Constants.TEXTURE_WRAP_ADDRESSMODE;
  tex.name = 'oceanDetailNormal';
  return tex;
}

/**
 * Foam texture.
 *   R = fine filament network — the SHAPE of foam lacing
 *   G = slow patchiness gating which regions are aerated at all
 *   B = fine bubble speckle for the interior of thick foam
 *   A = coarser network, combined with R so two scales hide the repeat
 */
export function createFoamTexture(scene: Scene, size = 512): Texture {
  const rng = new Rng('ocean-foam');
  // R: two nested cellular systems form the filament network that defines foam shape.
  // Their incommensurate cell counts keep the repeat hidden across kilometre-scale
  // sampling and give thin branches a few wider junctions.
  const fineNetwork = cellularEdgeNetwork(size, 19, rng, 10.5, 1.85);
  const mediumNetwork = cellularEdgeNetwork(size, 11, rng, 8.5, 1.48);
  // G: slow patchiness deciding which regions are aerated at all.
  const patches = fbm(size, 4, 4, rng, 0.5);
  // B: fine bubble speckle for the interior of thick foam.
  const bubbles = fbm(size, 26, 3, rng, 0.5);
  // A: a broad cellular skeleton survives longer as wind-stretched residual foam.
  const coarse = cellularEdgeNetwork(size, 6, rng, 6.5, 1.12);

  const data = new Uint8Array(size * size * 4);
  for (let i = 0; i < size * size; i++) {
    const o = i * 4;
    const network = Math.max(
      fineNetwork[i] as number,
      (mediumNetwork[i] as number) * 0.55,
    );
    data[o + 0] = Math.round(Math.min(1, network) * 255);
    data[o + 1] = Math.round(Math.min(1, Math.pow(patches[i] as number, 1.3)) * 255);
    data[o + 2] = Math.round(Math.min(1, bubbles[i] as number) * 255);
    data[o + 3] = Math.round(Math.min(1, coarse[i] as number) * 255);
  }

  const tex = RawTexture.CreateRGBATexture(data, size, size, scene, true, false, Texture.TRILINEAR_SAMPLINGMODE);
  tex.wrapU = Constants.TEXTURE_WRAP_ADDRESSMODE;
  tex.wrapV = Constants.TEXTURE_WRAP_ADDRESSMODE;
  tex.name = 'oceanFoam';
  return tex;
}

/**
 * Four independent cloud signals for the sky dome.
 *
 * R = broad weather-system mass
 * G = middle-scale billows
 * B = fine erosion
 * A = very broad stratus variation
 *
 * Keeping these scales independent matters: one fBm copied into several channels can
 * make a cloud darker, but it cannot give it a large body, rounded internal forms and a
 * torn edge at the same time.
 */
export function createCloudTexture(scene: Scene, size = 1024): Texture {
  const rng = new Rng('sky-clouds');
  /*
   * Domain-warped gradient noise, and a higher base frequency than before.
   *
   * The old field was value noise starting from a 3x3 lattice across the whole dome —
   * about 170 px per cell before the sky-dome projection magnified it further — so the
   * lattice showed as soft polygons, most visibly at night where the tonal range is
   * compressed into a few dark values. Gradient noise removes the plateaus, the warp
   * curdles the shapes, and starting higher keeps any remaining structure small.
   */
  const mass = warpedFbm(size, 5, 7, rng, 0.56, 0.09);
  const billows = warpedFbm(size, 9, 6, rng, 0.57, 0.06);
  const erosion = warpedFbm(size, 20, 5, rng, 0.52, 0.035);
  const stratus = warpedFbm(size, 4, 6, rng, 0.61, 0.05);
  /*
   * Half float (RGBA16F), because the sky shader multiplies this field hard.
   *
   * A 1.82x body gain followed by a smoothstep only 0.23 wide is roughly an 8x
   * amplification, so a single 8-bit texture step became a ~3% jump in cloud density and
   * drew a visible contour. At night, where the whole sky spans a handful of output
   * levels, those contours were unmistakable, and no dither amplitude both hid them and
   * stayed invisible itself. Half float steps are 8-16x finer, which puts the amplified
   * step below anything the display can show. This is also the one float format iOS
   * actually filters — RESEARCH_NOTES section 2 forbids 32-bit float textures but
   * blesses RGBA16F, the same envelope the FFT design assumes.
   *
   * The caps gate matters: a device that cannot filter or mip half-float textures gets
   * the old 8-bit field, and tells the shader to dither wide enough to dissolve 8-bit
   * contours instead of merely finishing off half-float ones.
   */
  const curved = (field: Float32Array, i: number, gamma: number): number =>
    Math.min(1, Math.pow(field[i] as number, gamma));

  const caps = scene.getEngine().getCaps();
  const halfFloat = !!(
    caps.textureHalfFloat && caps.textureHalfFloatLinearFiltering && caps.textureHalfFloatRender
  );

  let tex: RawTexture;
  if (halfFloat) {
    const data = new Uint16Array(size * size * 4);
    for (let i = 0; i < size * size; i++) {
      const o = i * 4;
      data[o + 0] = floatToHalf(curved(mass, i, 1.18));
      data[o + 1] = floatToHalf(curved(billows, i, 1.05));
      data[o + 2] = floatToHalf(curved(erosion, i, 0.92));
      data[o + 3] = floatToHalf(curved(stratus, i, 1.12));
    }
    tex = new RawTexture(
      data, size, size, Constants.TEXTUREFORMAT_RGBA, scene, true, false,
      Texture.TRILINEAR_SAMPLINGMODE, Constants.TEXTURETYPE_HALF_FLOAT,
    );
  } else {
    const data = new Uint8Array(size * size * 4);
    for (let i = 0; i < size * size; i++) {
      const o = i * 4;
      data[o + 0] = Math.round(curved(mass, i, 1.18) * 255);
      data[o + 1] = Math.round(curved(billows, i, 1.05) * 255);
      data[o + 2] = Math.round(curved(erosion, i, 0.92) * 255);
      data[o + 3] = Math.round(curved(stratus, i, 1.12) * 255);
    }
    tex = RawTexture.CreateRGBATexture(
      data, size, size, scene, true, false, Texture.TRILINEAR_SAMPLINGMODE,
    );
  }
  // The dome reads this to size its dither to the format's quantum (see sky.frag).
  tex.metadata = { cloudDither: halfFloat ? 0.0015 : 0.0085 };
  tex.wrapU = Constants.TEXTURE_WRAP_ADDRESSMODE;
  tex.wrapV = Constants.TEXTURE_WRAP_ADDRESSMODE;
  tex.name = 'skyClouds';
  return tex;
}
